import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion, useReducedMotion } from 'framer-motion';

const SCROLL_RESISTANCE = 96;
const TRANSITION_LOCK_MS = 720;
const TOUCH_RESISTANCE = 72;
const SCENE_REVEAL_EASE = [0.23, 1, 0.32, 1];

const sceneEntranceContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.09,
      delayChildren: 0.14,
    },
  },
};

export const scenePassthroughVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.09,
      delayChildren: 0.06,
    },
  },
};

const sceneRevealItemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { ease: SCENE_REVEAL_EASE, duration: 0.52 },
  },
};

const motionTags = {
  div: motion.div,
  section: motion.section,
  h1: motion.h1,
  h2: motion.h2,
  h3: motion.h3,
  p: motion.p,
  ul: motion.ul,
  footer: motion.footer,
};

const ViewportScenesContext = createContext(null);
const SceneIdContext = createContext(null);

export function useViewportScenes() {
  return useContext(ViewportScenesContext);
}

export function SceneReveal({ children, className = '', as = 'div' }) {
  const MotionEl = motionTags[as] || motion.div;

  return (
    <MotionEl className={className} variants={sceneRevealItemVariants}>
      {children}
    </MotionEl>
  );
}

export function SceneGroup({ children, className = '', as = 'div' }) {
  const MotionEl = motionTags[as] || motion.div;

  return (
    <MotionEl className={className} variants={scenePassthroughVariants}>
      {children}
    </MotionEl>
  );
}

function SceneEntrance({ sceneId, children, className = '' }) {
  const ctx = useViewportScenes();
  const prefersReducedMotion = useReducedMotion();
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  const sceneIndex = ctx?.sceneIds?.indexOf(sceneId) ?? -1;
  const isActive = ctx?.activeIndex === sceneIndex;
  const hasVisited = ctx?.visitedScenes?.has(sceneIndex) ?? false;
  const isNativeScroll = ctx?.isNativeScroll ?? false;

  useEffect(() => {
    if (!isNativeScroll || sceneIndex < 0) return undefined;

    const node = sectionRef.current?.closest('[data-viewport-scene]');
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
          setIsInView(true);
        }
      },
      { threshold: [0.45, 0.55] }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isNativeScroll, sceneIndex]);

  const shouldAnimate = isNativeScroll ? isInView : isActive;
  const animateState = hasVisited || shouldAnimate ? 'visible' : 'hidden';

  useEffect(() => {
    if (!shouldAnimate || hasVisited || sceneIndex < 0) return undefined;

    const timer = window.setTimeout(() => {
      ctx?.markSceneVisited?.(sceneIndex);
    }, 1800);

    return () => window.clearTimeout(timer);
  }, [shouldAnimate, hasVisited, sceneIndex, ctx]);

  const handleComplete = (definition) => {
    if (definition !== 'visible' || !shouldAnimate || hasVisited || sceneIndex < 0) return;
    ctx?.markSceneVisited?.(sceneIndex);
  };

  if (prefersReducedMotion) {
    return (
      <div ref={sectionRef} className={`scene-entrance w-full max-lg:my-auto ${className}`.trim()}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      ref={sectionRef}
      className={`scene-entrance flex w-full min-h-0 flex-col max-lg:my-auto gap-2.5 max-lg:gap-3 sm:gap-2.5 lg:gap-3 ${className}`.trim()}
      initial={hasVisited ? 'visible' : 'hidden'}
      animate={animateState}
      variants={sceneEntranceContainerVariants}
      onAnimationComplete={handleComplete}
    >
      {children}
    </motion.div>
  );
}

export function ViewportScene({ id, children, className = '', innerClassName = '' }) {
  return (
    <section
      id={id}
      data-viewport-scene
      className={`viewport-scene relative z-10 h-[100dvh] w-full shrink-0 snap-start snap-always ${className}`}
    >
      <div
        className={`viewport-scene__inner flex h-full min-h-0 w-full flex-col overflow-hidden max-lg:overscroll-contain max-lg:overflow-y-auto ${innerClassName}`}
      >
        <SceneIdContext.Provider value={id}>{children}</SceneIdContext.Provider>
      </div>
    </section>
  );
}

/** Full-viewport shell: nav offset + content vertically centered in the scene */
export function SceneShell({ children, className = '' }) {
  const sceneId = useContext(SceneIdContext);

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col px-4 pb-10 pt-[4.25rem] max-lg:px-4 max-lg:pb-10 sm:px-4 sm:pb-14 sm:pt-20 lg:px-8 lg:pb-14 lg:pt-[5.25rem] ${className}`}
    >
      <div className="mx-auto flex w-full max-w-7xl min-h-0 flex-1 flex-col justify-center max-lg:justify-start gap-2.5 max-lg:gap-3 sm:gap-2.5 lg:gap-3">
        <SceneEntrance sceneId={sceneId}>{children}</SceneEntrance>
      </div>
    </div>
  );
}

export function SceneHeader({
  eyebrow,
  title,
  titleHighlight,
  subtitle,
  className = '',
  compact = false,
}) {
  const titleClass = compact
    ? 'mt-0.5 text-lg font-bold leading-tight text-secondary-900 max-lg:text-xl sm:text-xl lg:text-2xl'
    : 'mt-1 text-2xl font-bold text-secondary-900 sm:text-3xl lg:text-4xl';

  const subtitleClass = compact
    ? 'mx-auto mt-2 max-w-2xl text-sm text-secondary-600 max-lg:sr-only lg:mt-3 lg:text-base'
    : 'mx-auto mt-2 max-w-2xl text-sm text-secondary-600 lg:mt-3 lg:text-base';

  return (
    <motion.div
      className={`text-center ${compact ? 'mb-2 shrink-0 lg:mb-3' : ''} ${className}`.trim()}
      variants={scenePassthroughVariants}
    >
      {eyebrow ? (
        <SceneReveal>
          <p className="micro text-orange-600 ">
            {eyebrow}
          </p>
        </SceneReveal>
      ) : null}
      <SceneReveal>
        <h2 className={titleClass}>
          {title}
          {titleHighlight ? (
            <span className="text-orange-600 "> {titleHighlight}</span>
          ) : null}
        </h2>
      </SceneReveal>
      {subtitle ? (
        <SceneReveal>
          <p className={subtitleClass}>{subtitle}</p>
        </SceneReveal>
      ) : null}
    </motion.div>
  );
}

/**
 * Finds the active scene's scrollable inner region that can consume a
 * vertical gesture in the given direction (delta > 0 = content scrolls down).
 * Returns null when the carousel should handle the gesture itself.
 */
function findScrollableSceneAncestor(target, container, delta) {
  let node = target instanceof Element ? target : null;
  while (node && node !== container) {
    if (
      node.classList?.contains('viewport-scene__inner') &&
      node.scrollHeight > node.clientHeight + 2 &&
      (delta > 0
        ? node.scrollTop + node.clientHeight < node.scrollHeight - 1
        : node.scrollTop > 1)
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export default function ViewportScenes({
  children,
  className = '',
  onSceneChange,
  sceneApiRef,
  renderBackground,
}) {
  const prefersReducedMotion = useReducedMotion();
  const containerRef = useRef(null);
  const activeIndexRef = useRef(0);
  const scrollAccumulator = useRef(0);
  const isTransitioning = useRef(false);
  const touchStartY = useRef(null);
  const touchAccumulator = useRef(0);

  const childArray = React.Children.toArray(children);
  const sceneCount = childArray.length;
  const sceneIds = useMemo(
    () => childArray.map((child) => child?.props?.id).filter(Boolean),
    [childArray]
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [visitedScenes, setVisitedScenes] = useState(() => new Set());
  activeIndexRef.current = activeIndex;

  const markSceneVisited = useCallback((index) => {
    setVisitedScenes((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  const goToIndex = useCallback(
    (index) => {
      const clamped = Math.max(0, Math.min(index, sceneCount - 1));
      if (clamped === activeIndexRef.current) {
        scrollAccumulator.current = 0;
        touchAccumulator.current = 0;
        return;
      }

      setActiveIndex(clamped);
      onSceneChange?.(clamped);
      scrollAccumulator.current = 0;
      touchAccumulator.current = 0;
    },
    [sceneCount, onSceneChange]
  );

  const scrollToScene = useCallback(
    (idOrIndex) => {
      if (typeof idOrIndex === 'number') {
        goToIndex(idOrIndex);
        return;
      }

      const idx = childArray.findIndex((child) => child?.props?.id === idOrIndex);
      if (idx >= 0) goToIndex(idx);
    },
    [childArray, goToIndex]
  );

  const tryAdvance = useCallback(
    (direction) => {
      const current = activeIndexRef.current;
      const nextIndex = direction > 0 ? current + 1 : current - 1;
      if (nextIndex < 0 || nextIndex >= sceneCount) {
        scrollAccumulator.current = 0;
        touchAccumulator.current = 0;
        return;
      }

      isTransitioning.current = true;
      goToIndex(nextIndex);
      window.setTimeout(() => {
        isTransitioning.current = false;
      }, TRANSITION_LOCK_MS);
    },
    [goToIndex, sceneCount]
  );

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    const onWheel = (event) => {
      if (isTransitioning.current) {
        event.preventDefault();
        return;
      }

      if (findScrollableSceneAncestor(event.target, container, event.deltaY)) {
        return;
      }

      const direction = event.deltaY > 0 ? 1 : -1;
      event.preventDefault();
      scrollAccumulator.current += event.deltaY;

      if (Math.abs(scrollAccumulator.current) < SCROLL_RESISTANCE) return;
      tryAdvance(direction);
    };

    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, [prefersReducedMotion, tryAdvance]);

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    const onTouchStart = (event) => {
      touchStartY.current = event.touches[0]?.clientY ?? null;
      touchAccumulator.current = 0;
    };

    const onTouchMove = (event) => {
      if (touchStartY.current == null || isTransitioning.current) return;

      const currentY = event.touches[0]?.clientY;
      if (currentY == null) return;

      const delta = touchStartY.current - currentY;
      const direction = delta > 0 ? 1 : -1;

      if (Math.abs(delta) < 4) return;

      if (findScrollableSceneAncestor(event.target, container, delta)) {
        touchStartY.current = currentY;
        touchAccumulator.current = 0;
        return;
      }

      event.preventDefault();

      touchAccumulator.current += delta;
      touchStartY.current = currentY;

      if (Math.abs(touchAccumulator.current) < TOUCH_RESISTANCE) return;
      tryAdvance(direction);
    };

    const onTouchEnd = () => {
      touchStartY.current = null;
      touchAccumulator.current = 0;
    };

    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd);
    container.addEventListener('touchcancel', onTouchEnd);

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [prefersReducedMotion, tryAdvance]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const sections = container.querySelectorAll('[data-viewport-scene]');
    const inner = sections[activeIndex]?.querySelector('.viewport-scene__inner');
    if (inner) inner.scrollTop = 0;
  }, [activeIndex]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (prefersReducedMotion) return;
      if (isTransitioning.current) return;

      const tag = event.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || event.target?.isContentEditable) {
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        event.preventDefault();
        tryAdvance(1);
      } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        tryAdvance(-1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [prefersReducedMotion, tryAdvance]);

  const contextValue = useMemo(
    () => ({
      activeIndex,
      sceneCount,
      sceneIds,
      visitedScenes,
      markSceneVisited,
      scrollToScene,
      goToIndex,
      isNativeScroll: prefersReducedMotion,
    }),
    [
      activeIndex,
      sceneCount,
      sceneIds,
      visitedScenes,
      markSceneVisited,
      scrollToScene,
      goToIndex,
      prefersReducedMotion,
    ]
  );

  useEffect(() => {
    if (!sceneApiRef) return;
    sceneApiRef.current = contextValue;
  }, [sceneApiRef, contextValue]);

  useEffect(() => {
    if (!prefersReducedMotion) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    const sections = container.querySelectorAll('[data-viewport-scene]');
    if (!sections.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        let bestIndex = activeIndexRef.current;
        let bestRatio = 0;

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = Array.from(sections).indexOf(entry.target);
          if (index < 0) return;
          if (entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestIndex = index;
          }
        });

        if (bestRatio >= 0.45 && bestIndex !== activeIndexRef.current) {
          setActiveIndex(bestIndex);
          onSceneChange?.(bestIndex);
        }
      },
      { root: container, threshold: [0.45, 0.55, 0.65] }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [prefersReducedMotion, sceneCount, onSceneChange]);

  if (prefersReducedMotion) {
    return (
      <ViewportScenesContext.Provider value={contextValue}>
        <div
          ref={containerRef}
          className={`viewport-scenes viewport-scenes--native relative h-[100dvh] snap-y snap-mandatory overflow-y-auto ${className}`}
        >
          {renderBackground?.(sceneCount, activeIndex)}
          {children}
        </div>
      </ViewportScenesContext.Provider>
    );
  }

  return (
    <ViewportScenesContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className={`viewport-scenes relative h-[100dvh] overflow-hidden ${className}`}
      >
        <motion.div
          className="viewport-scenes-track relative flex w-full flex-col"
          animate={{ y: `calc(${activeIndex} * -100dvh)` }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          style={{ height: `${sceneCount * 100}dvh` }}
        >
          {renderBackground?.(sceneCount, activeIndex)}
          {children}
        </motion.div>
      </div>
    </ViewportScenesContext.Provider>
  );
}
